import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Check, Loader2, XCircle, ArrowRight } from "lucide-react";

export default function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId && user) {
      pollStatus();
    } else if (!sessionId) {
      setStatus("error");
    }
  }, [sessionId, user]);

  const pollStatus = async () => {
    if (pollCount >= 10) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(
        `${API}/subscription/checkout-status/${sessionId}`,
        
      );

      // Success conditions: subscription is active, trialing, or session is complete
      if (
        response.data.subscription_status === "active" || 
        response.data.payment_status === "paid" ||
        response.data.payment_status === "no_payment_required" ||  // Trial subscriptions
        response.data.status === "complete"
      ) {
        setStatus("success");
      } else if (response.data.status === "expired") {
        setStatus("error");
      } else {
        // Keep polling
        setTimeout(() => {
          setPollCount(prev => prev + 1);
          pollStatus();
        }, 2000);
      }
    } catch (error) {
      console.error("Status check error:", error);
      // Continue polling even on error
      setTimeout(() => {
        setPollCount(prev => prev + 1);
        pollStatus();
      }, 2000);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="py-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-gold mx-auto mb-6" />
            <h2 className="font-serif text-2xl font-bold mb-2">Setting Up Your Subscription</h2>
            <p className="text-muted-foreground">Please wait while we activate your account...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="subscription-error">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="font-serif text-2xl font-bold mb-2">Something Went Wrong</h2>
            <p className="text-muted-foreground mb-6">
              We couldn't verify your subscription. Please try again or contact support.
            </p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => navigate("/subscribe")} className="bg-gold text-primary-foreground hover:bg-gold/90">
                Try Again
              </Button>
              <Button variant="outline" onClick={() => navigate("/")}>
                Go Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" data-testid="subscription-success">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-6">
            <Check className="h-8 w-8 text-gold" />
          </div>
          <h2 className="font-serif text-2xl font-bold mb-2">Welcome to Bar Songz!</h2>
          <p className="text-muted-foreground mb-2">
            Your subscription is now active.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            You now have unlimited access to all our law school content.
          </p>
          
          <div className="space-y-3">
            <Button 
              className="w-full bg-gold text-primary-foreground hover:bg-gold/90"
              onClick={() => navigate("/library")}
              data-testid="go-to-library-btn"
            >
              Start Learning
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate("/account")}
            >
              Manage Subscription
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
